import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/shop';
import { brand, formatMoney } from '../brand/brand';
import { usePageTitle } from '../a11y/usePageTitle';
import { QrCode } from '../components/QrCode';
import type { MyVoucher } from '../api/types';

/**
 * The paper voucher. SOW TC-01: "Customer has no smartphone (paper voucher) -
 * server-held balance referenced by a printed code; a 4-digit PIN (not printed)
 * gates redemption; merchant looks it up via the portal. Photocopies are worthless
 * once the balance is drained."
 *
 * WHAT IS PRINTED: the code, as text and as a QR the counter can scan, and what it
 * is for. WHAT IS NOT: the PIN - ever - and the balance as a promise. The balance
 * lives on the server and is what the till reads; a figure printed today is wrong
 * after the first spend, and a paper that says "$50" invites an argument at the
 * counter when $12 is left. So it says what the voucher was worth when printed, and
 * labels it that way.
 *
 * The page prints clean: header, footer and the buttons are hidden by the print
 * stylesheet (styles.css, @media print).
 */
export function VoucherPrint() {
  const { voucherId } = useParams<{ voucherId: string }>();
  const [voucher, setVoucher] = useState<MyVoucher | null>(null);
  const [error, setError] = useState(false);

  usePageTitle(voucher === null ? null : `Print - ${voucher.dealTitle}`);

  useEffect(() => {
    if (voucherId === undefined) return;
    let cancelled = false;

    api.voucher(voucherId)
      .then((v) => { if (!cancelled) setVoucher(v); })
      .catch(() => { if (!cancelled) setError(true); });

    return () => { cancelled = true; };
  }, [voucherId]);

  if (error) {
    return (
      <div className="panel narrow" style={{ marginTop: 40 }}>
        <h2>Voucher not found</h2>
        <Link to="/vouchers" className="btn" style={{ marginTop: 16 }}>Back to my vouchers</Link>
      </div>
    );
  }

  if (voucher === null) {
    return <p className="muted" style={{ padding: '48px 0' }} role="status">Loading…</p>;
  }

  const usable = voucher.status === 'active' && voucher.balanceCents > 0 && voucher.code !== null;

  if (!usable || voucher.code === null) {
    return (
      <div className="panel narrow" style={{ marginTop: 40 }}>
        <h2>This voucher cannot be printed</h2>
        <p className="muted">Only a voucher with value left can be printed.</p>
        <Link to={`/vouchers/${voucher.id}`} className="btn" style={{ marginTop: 16 }}>Back to the voucher</Link>
      </div>
    );
  }

  return (
    <div className="stack narrow" style={{ gap: 16, marginBlock: 24 }}>
      <div className="row no-print" style={{ gap: 8, justifyContent: 'space-between' }}>
        <Link to={`/vouchers/${voucher.id}`} className="btn secondary">Back</Link>
        <button type="button" className="btn" onClick={() => window.print()}>Print</button>
      </div>

      <article className="paper-voucher">
        <header className="paper-head">
          <span className="paper-brand">{brand.name}</span>
          <span className="faint">Voucher</span>
        </header>

        <p className="paper-business">{voucher.businessName}</p>
        <h1 className="paper-title">{voucher.dealTitle}</h1>

        <div className="paper-body">
          <QrCode value={voucher.code} size={170} label="Voucher code" />

          <div className="stack" style={{ gap: 6 }}>
            <p className="faint" style={{ margin: 0 }}>Voucher code</p>
            <p className="paper-code" aria-label={voucher.code.split('').join(' ')}>{voucher.code}</p>

            <p className="faint" style={{ margin: 0 }}>
              Worth {formatMoney(voucher.balanceCents)} when printed on {new Date().toLocaleDateString()}.
              The shop checks what is left when you use it.
            </p>
            <p className="faint" style={{ margin: 0 }}>
              {voucher.lockedToBranch === null
                ? `Use at any ${voucher.businessName} location`
                : `Use at ${voucher.lockedToBranch}`}
              {' '}until {new Date(voucher.validTo).toLocaleDateString()}.
            </p>
          </div>
        </div>

        <footer className="paper-foot">
          <strong>Your PIN is not on this paper.</strong>{' '}
          {voucher.hasPin
            ? 'The cashier will ask for your 4-digit PIN - it was sent to you by email. Without it this code cannot be spent, so a copy of this page is no use to anyone else.'
            : 'Keep this paper safe: this voucher has no PIN, so the code alone can spend it.'}
        </footer>
      </article>

      <p className="hint no-print">
        Tip: print it, or save it as a PDF from the print window. If you lose it, the value is safe -
        sign in to see the code again, and reset your PIN if you think someone else has it.
      </p>
    </div>
  );
}
